import { z } from 'zod';
import { EngagementStageSchema } from '../domain/engagement.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema } from './common.js';
/**
 * A recorded human decision at an approval gate (§5).
 *
 * Approvals are append-only. A decision is never edited or withdrawn — a later decision is a
 * new record — so the trail shows what was decided, by whom, and against which scope.
 */
export const ApprovalGateSchema = z.enum(['SCOPE', 'PLAN', 'REPORT']);
export const ApprovalDecisionSchema = z.enum(['APPROVED', 'DECLINED']);
/** The stage each gate is waiting in, and where each decision sends it. */
export const GATE_STAGES = {
    SCOPE: {
        pending: 'SCOPE_PENDING_APPROVAL',
        approved: 'SCOPED',
        // Declining returns it to the author to change, rather than killing the engagement.
        declined: 'DRAFT',
    },
    PLAN: { pending: 'PLAN_PENDING_APPROVAL', approved: 'ACTIVE', declined: 'TRIAGE' },
    REPORT: { pending: 'REPORT_PENDING_APPROVAL', approved: 'PUBLISHED', declined: 'TRIAGE' },
};
/** The gate an engagement is currently waiting at, or null if it is not waiting. */
export function gateForStage(stage) {
    const found = ApprovalGateSchema.options.find((gate) => GATE_STAGES[gate].pending === stage);
    return found ?? null;
}
export const ApprovalSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    engagementId: IdSchema,
    gate: ApprovalGateSchema,
    decision: ApprovalDecisionSchema,
    /**
     * The scope version this decision was made against.
     *
     * This is what stops an approval from outliving what it approved: if the scope is revised
     * afterwards, the new version has no approval, and the recorded one still names exactly
     * what was reviewed.
     */
    scopeVersion: z.number().int().positive(),
    /**
     * SHA-256 of the scope version's canonical JSON, taken at the moment of the decision.
     *
     * What makes the decision checkable later: re-hash the stored scope and compare. A scope
     * changed after it was approved no longer matches. It shows *that* something changed, not who
     * changed it — tamper-evident, not a signature. Absent on decisions recorded before hashing.
     */
    scopeDigest: z.string().length(64).optional(),
    /** REPORT gate only: the report version decided on, and its hash. */
    reportVersion: z.number().int().positive().optional(),
    reportDigest: z.string().length(64).optional(),
    approverUserId: UserIdSchema,
    /** From the verified ID token, so the trail names a person rather than an opaque id. */
    approverEmail: z.string().max(320).optional(),
    at: IsoDateTimeSchema,
    /** Free text from the approver. Shown in the trail; never interpreted. */
    note: z.string().trim().max(1000).optional(),
});
/**
 * POST /v1/engagements/{id}/approvals
 *
 * `gate` and `scopeVersion` are required and are checked against the engagement's current
 * state. They are not redundant: they say *what the approver was looking at*. If the scope
 * was revised, or the engagement moved on, while the approval screen was open, the decision
 * is refused rather than applied to something the approver never saw.
 */
export const CreateApprovalRequestSchema = z
    .strictObject({
    gate: ApprovalGateSchema,
    decision: ApprovalDecisionSchema,
    scopeVersion: z.number().int().positive(),
    /**
     * REPORT gate only, and required there: the report version the approver read. A report
     * regenerated while the approval screen was open is refused rather than inheriting a
     * decision about its predecessor.
     */
    reportVersion: z.number().int().positive().optional(),
    note: z.string().trim().max(1000).optional(),
})
    .refine((body) => (body.gate === 'REPORT') === (body.reportVersion !== undefined), {
    message: 'reportVersion is required at the REPORT gate and not accepted at any other',
    path: ['reportVersion'],
});
export const ListApprovalsResponseSchema = z.object({ items: z.array(ApprovalSchema) });
/** Engagements waiting at a gate, for the approvals queue. */
export const PendingApprovalSchema = z.object({
    engagementId: IdSchema,
    engagementName: z.string(),
    environment: z.string(),
    stage: EngagementStageSchema,
    gate: ApprovalGateSchema,
    scopeVersion: z.number().int().positive(),
    updatedAt: IsoDateTimeSchema,
});
export const PendingApprovalsResponseSchema = z.object({ items: z.array(PendingApprovalSchema) });
//# sourceMappingURL=approval.js.map